/**
 * Notification Governance Components
 * 
 * Unified panels for Setup Tasks, Month-End Actions, and Notification Center.
 * All dashboard alerts flow through the Action Engine API.
 * 
 * Core Principles:
 * - No module independently renders banners
 * - All alerts originate from action_engine
 * - Max 2 panels on dashboard
 * - No permanent dismiss for pending financial items
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { isNativeApp, setNativeBadge } from '../../utils/nativeBridge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Bell,
  X,
  ChevronRight,
  Clock,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Settings,
  Calendar,
  DollarSign,
  Landmark,
  Building2,
  Receipt,
  Package,
  TrendingUp,
  Loader2,
  BellOff,
  History,
  ClipboardList,
  Scale
} from 'lucide-react';

// Severity color mapping
const severityConfig = {
  Critical: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', icon: 'text-red-600', badge: 'bg-red-100 text-red-700 border-red-300' },
  High: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700 border-amber-300' },
  Medium: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
  Low: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', icon: 'text-gray-500', badge: 'bg-gray-100 text-gray-600 border-gray-200' }
};

// Module icon mapping
const moduleIcons = {
  loans: Landmark,
  investments: TrendingUp,
  fixed_assets: Building2,
  prepayments: Receipt,
  payment_gateway: DollarSign,
  opening_balance: Settings,
  period_close: Calendar,
  bank_feed: Clock,
  reconciliation: CheckCircle2,
  inventory: Package,
  bills: Receipt
};

/**
 * Single Action Item Component
 */
const ActionItem = ({ item, onSnooze, onDismiss, onComplete, compact = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const config = severityConfig[item.severity] || severityConfig.Medium;
  const IconComponent = moduleIcons[item.source_module] || AlertCircle;
  
  const handleAction = () => {
    if (item.action_url) {
      navigate(item.action_url);
    }
  };
  
  const handleSnooze = async (hours) => {
    setLoading(true);
    try {
      await onSnooze(item.dedupe_key, hours);
      toast.success(`Snoozed for ${hours === 24 ? '1 day' : '7 days'}`);
    } catch (error) {
      toast.error('Failed to snooze');
    } finally {
      setLoading(false);
    }
  };
  
  if (compact) {
    return (
      <div 
        className={`flex items-center justify-between p-3 rounded-lg border ${config.border} ${config.bg} hover:shadow-sm transition-shadow cursor-pointer`}
        onClick={handleAction}
        data-testid={`action-item-${item.dedupe_key}`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.bg}`}>
            <IconComponent className={`w-4 h-4 ${config.icon}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-medium text-sm truncate ${config.text}`}>{item.title}</p>
            <p className="text-xs text-gray-500 truncate">{item.impact_statement}</p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </div>
    );
  }
  
  return (
    <div 
      className={`p-4 rounded-lg border-2 ${config.border} ${config.bg}`}
      data-testid={`action-item-${item.dedupe_key}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.bg}`}>
            <IconComponent className={`w-5 h-5 ${config.icon}`} />
          </div>
          <div className="flex-1">
            <h4 className={`font-semibold flex items-center gap-2 ${config.text}`}>
              {item.title}
              <Badge variant="outline" className={config.badge}>
                {item.severity}
              </Badge>
            </h4>
            <p className={`text-sm mt-1 ${config.text} opacity-90`}>{item.description}</p>
            <p className="text-xs text-gray-500 mt-2 italic">{item.impact_statement}</p>
            
            {item.deadline_at && (
              <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                <span>Deadline: {new Date(item.deadline_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            className={item.severity === 'Critical' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#108981] hover:bg-[#0d7069]'}
            onClick={handleAction}
          >
            {item.cta_text}
          </Button>
          
          {item.snooze_allowed && (
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="text-xs px-2 py-1 h-7"
                onClick={() => handleSnooze(24)}
                disabled={loading}
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : '24h'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-xs px-2 py-1 h-7"
                onClick={() => handleSnooze(168)}
                disabled={loading}
              >
                7d
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Setup Tasks Panel
 * 
 * Contains:
 * - Loans with default interest
 * - Investments with default return
 * - Fixed assets using default useful life
 * - Prepayments using default coverage
 * - Payment gateway not connected
 * - Opening balance incomplete
 */
export const SetupTasksPanel = ({ className = '', onRefresh }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewNeededCount, setReviewNeededCount] = useState(0);
  const [reconSummary, setReconSummary] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('setup_tasks_collapsed');
      if (stored) {
        const { date, collapsed: wasCollapsed } = JSON.parse(stored);
        const today = new Date().toISOString().slice(0, 10);
        if (date === today && wasCollapsed) return true;
      }
    } catch (e) { /* ignore */ }
    return false;
  });
  const [showAllTasksDialog, setShowAllTasksDialog] = useState(false);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('setup_tasks_collapsed', JSON.stringify({
      date: new Date().toISOString().slice(0, 10),
      collapsed: next
    }));
  };
  
  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/setup-tasks`);
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch setup tasks:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReviewNeeded = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bank-feeds/stats`);
      setReviewNeededCount(response.data?.review_needed || 0);
    } catch (error) {
      // Silently fail - stats may not be available
    }
  }, []);

  const fetchReconSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bank-reconciliation/summary/all`);
      setReconSummary(response.data);
    } catch (error) {
      // Silently fail
    }
  }, []);
  
  useEffect(() => {
    fetchTasks();
    fetchReviewNeeded();
    fetchReconSummary();
    
    // Set up polling interval to refresh tasks every 30 seconds
    const pollInterval = setInterval(() => {
      fetchTasks();
      fetchReviewNeeded();
      fetchReconSummary();
    }, 30000);
    
    // Listen for custom events when items are updated from dashboards
    const handleSetupTaskUpdate = () => {
      fetchTasks();
    };
    window.addEventListener('setup-task-updated', handleSetupTaskUpdate);
    
    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('setup-task-updated', handleSetupTaskUpdate);
    };
  }, [fetchTasks]);
  
  const handleSnooze = async (dedupeKey, hours) => {
    await axios.post(`${API}/notifications/snooze`, { dedupe_key: dedupeKey, hours });
    fetchTasks();
  };
  
  const handleDismiss = async (dedupeKey, permanent = false) => {
    await axios.post(`${API}/notifications/dismiss`, { dedupe_key: dedupeKey, permanent });
    fetchTasks();
  };
  
  // Expose refresh function to parent
  useEffect(() => {
    if (onRefresh) {
      onRefresh.current = fetchTasks;
    }
  }, [onRefresh, fetchTasks]);
  
  if (loading) return null;
  
  // Count all open items across categories
  const reconUnmatched = reconSummary?.total_unmatched || 0;
  const reconAccounts = reconSummary?.needs_attention_count || 0;
  const visibleTasks = tasks.filter(t => ['Critical', 'High'].includes(t.severity));
  const totalTaskCount = tasks.length;
  const highPriorityCount = visibleTasks.length;
  const totalOpenItems = highPriorityCount + (reviewNeededCount > 0 ? 1 : 0) + (reconUnmatched > 0 ? 1 : 0);
  
  // Show panel if there's anything to show
  if (totalOpenItems === 0) return null;
  
  return (
    <Card className={`border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 ${className}`} data-testid="setup-tasks-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
            <ClipboardList className="w-5 h-5" />
            Open Tasks
            <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
              {totalOpenItems} pending
            </Badge>
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleCollapsed}
            className="text-blue-700 border-blue-300 hover:bg-blue-100 font-semibold gap-1.5"
            data-testid="toggle-open-tasks"
          >
            {collapsed ? (
              <><ChevronRight className="w-3.5 h-3.5 rotate-90" /> Expand</>
            ) : (
              <><ChevronRight className="w-3.5 h-3.5 -rotate-90" /> Collapse</>
            )}
          </Button>
        </div>
        <p className="text-sm text-blue-600">
          Outstanding items requiring your attention
        </p>
      </CardHeader>
      
      {!collapsed && (
        <CardContent className="pt-2 space-y-3">
          {/* Review Needed CTA */}
          {reviewNeededCount > 0 && (
            <div
              onClick={() => navigate('/bank-feeds?filter=pending')}
              className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer hover:bg-amber-100 transition-colors"
              data-testid="review-needed-cta"
            >
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-4 h-4 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-800">
                  {reviewNeededCount} transaction{reviewNeededCount !== 1 ? 's' : ''} from Bank Transactions pending review
                </p>
                <p className="text-xs text-amber-600">Review and categorise to keep your books accurate</p>
              </div>
              <ChevronRight className="w-4 h-4 text-amber-400 flex-shrink-0" />
            </div>
          )}

          {/* Reconciliation CTA */}
          {reconUnmatched > 0 && (
            <div
              onClick={() => navigate('/bank-feeds?tab=reconciliation')}
              className="flex items-center gap-3 bg-violet-50 border border-violet-200 rounded-lg p-3 cursor-pointer hover:bg-violet-100 transition-colors"
              data-testid="reconciliation-needed-cta"
            >
              <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                <Scale className="w-4 h-4 text-violet-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-violet-800">
                  {reconUnmatched} unreconciled item{reconUnmatched !== 1 ? 's' : ''} across {reconAccounts} bank account{reconAccounts !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-violet-600">Reconcile your bank statements to ensure your books match the bank</p>
              </div>
              <ChevronRight className="w-4 h-4 text-violet-400 flex-shrink-0" />
            </div>
          )}

          {visibleTasks.slice(0, 5).map((task) => (
            <ActionItem
              key={task.dedupe_key}
              item={task}
              onSnooze={handleSnooze}
              onDismiss={handleDismiss}
              compact={visibleTasks.length > 2}
            />
          ))}
          
          {/* Show "View all" link if there are more tasks than displayed */}
          {(visibleTasks.length > 5 || totalTaskCount > highPriorityCount) && (
            <div className="text-center pt-2">
              <Button 
                variant="link" 
                className="text-blue-600"
                onClick={() => setShowAllTasksDialog(true)}
              >
                View all {totalTaskCount} tasks
              </Button>
            </div>
          )}
        </CardContent>
      )}
      
      {/* All Tasks Dialog */}
      <Dialog open={showAllTasksDialog} onOpenChange={setShowAllTasksDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              All Open Tasks ({tasks.length})
            </DialogTitle>
            <DialogDescription>
              Items requiring your attention to ensure accurate financial reporting
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {tasks.map((item) => (
              <ActionItem 
                key={item.id} 
                item={item} 
                onSnooze={handleSnooze}
                onDismiss={handleDismiss}
                onAction={() => {
                  setShowAllTasksDialog(false);
                  navigate(item.action_url);
                }}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/**
 * Month-End Action Center
 * 
 * Contains:
 * - Period lock readiness
 * - Unmatched transactions
 * - Reconciliation needed
 * - Closing stock required (if goods business)
 * - Tax readiness
 */
export const MonthEndActionCenter = ({ className = '' }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  
  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/month-end`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch month-end status:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleSnooze = async (dedupeKey, hours) => {
    await axios.post(`${API}/notifications/snooze`, { dedupe_key: dedupeKey, hours });
    fetchData();
  };
  
  if (loading) return null;
  
  // DON'T SHOW if there are no items to address
  if (!data?.show_panel) return null;
  
  const items = data.items || [];
  if (items.length === 0) return null;
  
  const hasCritical = items.some(i => i.severity === 'Critical');
  const borderColor = hasCritical ? 'border-red-300' : 'border-amber-300';
  const bgGradient = hasCritical ? 'from-red-50 to-orange-50' : 'from-amber-50 to-orange-50';
  const headerColor = hasCritical ? 'text-red-800' : 'text-amber-800';
  const subColor = hasCritical ? 'text-red-600' : 'text-amber-600';
  
  // Navigate to dashboard
  const handleReviewPeriod = () => {
    navigate('/dashboard');
  };
  
  return (
    <Card className={`border-2 ${borderColor} bg-gradient-to-r ${bgGradient} ${className}`} data-testid="month-end-action-center">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className={`text-lg flex items-center gap-2 ${headerColor}`}>
            <Calendar className="w-5 h-5" />
            Period Close Reminder
            <Badge variant="outline" className={hasCritical ? 'bg-red-100 text-red-700 border-red-300' : 'bg-amber-100 text-amber-700 border-amber-300'}>
              {items.length} item{items.length !== 1 ? 's' : ''} to address
            </Badge>
          </CardTitle>
          <Button
            size="sm"
            onClick={handleReviewPeriod}
            className={hasCritical ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
            data-testid="see-all-items-btn"
          >
            See All Items
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <p className={`text-sm ${subColor}`}>
          {data.current_period} • {items.length} open item{items.length !== 1 ? 's' : ''} need{items.length === 1 ? 's' : ''} attention before period close
        </p>
      </CardHeader>
      
      {!collapsed && items.length > 0 && (
        <CardContent className="pt-2 space-y-2">
          {/* Show preview of first 3 items */}
          {items.slice(0, 3).map((item) => (
            <div 
              key={item.dedupe_key}
              className="flex items-center justify-between p-2 rounded-lg bg-white/50 hover:bg-white/80 transition-colors cursor-pointer"
              onClick={() => item.action_url && navigate(item.action_url)}
            >
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  item.severity === 'Critical' ? 'bg-red-100' : 
                  item.severity === 'High' ? 'bg-orange-100' : 'bg-amber-100'
                }`}>
                  {React.createElement(moduleIcons[item.source_module] || AlertCircle, {
                    className: `w-3 h-3 ${
                      item.severity === 'Critical' ? 'text-red-600' : 
                      item.severity === 'High' ? 'text-orange-600' : 'text-amber-600'
                    }`
                  })}
                </div>
                <span className="text-sm font-medium text-gray-700">{item.title}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
          ))}
          
          {items.length > 3 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className={`w-full ${subColor} hover:bg-white/50`}
              onClick={handleReviewPeriod}
            >
              View all {items.length} items
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
};

/**
 * Notification Center (Bell Icon Dropdown)
 * 
 * Full notification center with:
 * - Active items
 * - Snoozed items
 * - Dismissed items (history)
 * - Preferences link
 */
export const NotificationCenter = ({ variant = 'desktop', compact = false }) => {
  const isMobile = variant === 'mobile';
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('alerts');

  // Push notification state
  const [pushNotifs, setPushNotifs] = useState([]);
  const [pushUnread, setPushUnread] = useState(0);
  const [pushPermission, setPushPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  // Users who already have the native app (or are inside it) get their alerts from the app - no install/enable nag.
  const [hasNativeApp, setHasNativeApp] = useState(() => isNativeApp());
  useEffect(() => {
    if (isNativeApp()) return;
    axios.get(`${API}/push-notifications/native-device/status`)
      .then((r) => setHasNativeApp(!!r.data?.has_native_app))
      .catch(() => {});
  }, []);

  // Fetch action-engine data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications/center`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch notification center:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Instagram-style app-icon badge. Best-effort; unsupported browsers noop.
  const applyAppBadge = useCallback((count) => {
    // Native wrapper (installed iOS/Android app): icon badge is driven only by our setBadge calls.
    setNativeBadge(count);
    if (typeof navigator === 'undefined' || !('setAppBadge' in navigator)) return;
    try {
      if (count > 0) navigator.setAppBadge(count);
      else navigator.clearAppBadge();
    } catch { /* platform noop */ }
  }, []);

  // Fetch push notification history
  const fetchPushHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/push-notifications/history?limit=30`);
      const notifs = response.data.notifications || [];
      const unread = response.data.unread_count || 0;
      setPushNotifs(notifs);
      setPushUnread(unread);
      applyAppBadge(unread);
    } catch {
      // silent - endpoint may not exist yet
    }
  }, [applyAppBadge]);

  // Poll for badge counts.
  // iter873 - NotificationCenter is mounted twice in DashboardLayout
  // (desktop + mobile), but only ONE is visible at a time. Skip fetching
  // in the variant that does not match the current viewport so we don't
  // double the API load on every page navigation.
  // F5 (perf audit) - Pause polling entirely when the tab is hidden.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const matchesMobile = window.matchMedia('(max-width: 767px)').matches;
      if (isMobile !== matchesMobile) return; // wrong variant for this viewport
    }
    let timerId = null;
    const tick = () => {
      fetchData();
      fetchPushHistory();
      timerId = setTimeout(tick, 60000);
    };
    const stopTimer = () => {
      if (timerId) { clearTimeout(timerId); timerId = null; }
    };
    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.hidden) {
        stopTimer();
      } else if (!timerId) {
        // Fire immediately on refocus so the badge is fresh.
        tick();
      }
    };
    tick();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      stopTimer();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [fetchData, fetchPushHistory, isMobile]);

  useEffect(() => {
    if (open) {
      fetchData();
      fetchPushHistory();
    }
  }, [open, fetchData, fetchPushHistory]);

  const handleSnooze = async (dedupeKey, hours) => {
    await axios.post(`${API}/notifications/snooze`, { dedupe_key: dedupeKey, hours });
    fetchData();
  };

  const handleDismiss = async (dedupeKey, permanent = false) => {
    await axios.post(`${API}/notifications/dismiss`, { dedupe_key: dedupeKey, permanent });
    fetchData();
  };

  const handleMarkPushRead = async () => {
    try {
      await axios.post(`${API}/push-notifications/mark-read?mark_all=true`);
      setPushUnread(0);
      setPushNotifs(prev => prev.map(n => ({ ...n, read: true })));
      applyAppBadge(0);
    } catch { /* silent */ }
  };

  // Per-item mark-read (row click). Does NOT force navigation - user can
  // read the full body without leaving the page.
  const markOneRead = async (notif) => {
    if (!notif?.id || notif.read) return;
    try {
      const res = await axios.post(
        `${API}/push-notifications/mark-read?mark_all=false&notification_id=${encodeURIComponent(notif.id)}`
      );
      const nextUnread = typeof res.data?.unread_count === 'number'
        ? res.data.unread_count
        : Math.max(0, pushUnread - 1);
      setPushUnread(nextUnread);
      setPushNotifs(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      applyAppBadge(nextUnread);
    } catch { /* silent */ }
  };

  const handleEnablePush = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    const result = await Notification.requestPermission();
    setPushPermission(result);
    if (result === 'granted') {
      try {
        const { data: vapidData } = await axios.get(`${API}/push-notifications/vapid-key`);
        if (!vapidData.public_key) return;
        const registration = await navigator.serviceWorker.ready;
        let sub = await registration.pushManager.getSubscription();
        if (!sub) {
          const padding = '='.repeat((4 - (vapidData.public_key.length % 4)) % 4);
          const b64 = (vapidData.public_key + padding).replace(/-/g, '+').replace(/_/g, '/');
          const rawData = window.atob(b64);
          const key = new Uint8Array(rawData.length);
          for (let i = 0; i < rawData.length; ++i) key[i] = rawData.charCodeAt(i);
          sub = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
        }
        const subJson = sub.toJSON();
        await axios.post(`${API}/push-notifications/subscribe`, { endpoint: subJson.endpoint, keys: subJson.keys });
        // Send a test notification
        await axios.post(`${API}/push-notifications/test`);
        setTimeout(fetchPushHistory, 1500);
      } catch (err) {
        console.error('Push subscribe failed:', err);
      }
    }
  };

  const activeCount = data?.summary?.active_count || 0;
  // iter1111 - the Bell now covers push alerts only; the Task Bar owns
  // "things to do". Badge reflects push unread; task count no longer
  // inflates the bell badge (avoids double-counting with the Task Bar).
  const totalBadge = pushUnread;
  const hasCritical = data?.active?.some(a => a.severity === 'Critical');

  // Push notification type icons & colors
  const pushTypeConfig = {
    invoice_paid: { icon: DollarSign, color: 'text-green-600', bg: 'bg-green-50' },
    payment_received: { icon: DollarSign, color: 'text-green-600', bg: 'bg-green-50' },
    bill_due: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    bill_overdue: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
    settlement_complete: { icon: CheckCircle2, color: 'text-blue-600', bg: 'bg-blue-50' },
    bank_feed_complete: { icon: Landmark, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    low_cash: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
    general: { icon: Bell, color: 'text-gray-600', bg: 'bg-gray-50' },
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setNativeBadge(0); }}>
      <PopoverTrigger asChild>
        {isMobile ? (
          <button
            style={{ width: '45px', height: '45px' }}
            className="relative inline-flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all"
            data-testid="notification-center-trigger"
          >
            <Bell style={{ width: '29px', height: '29px' }} />
            {totalBadge > 0 && (
              <span className={`absolute top-0.5 right-0.5 w-5 h-5 rounded-full text-xs flex items-center justify-center text-white font-medium ${hasCritical ? 'bg-red-500 animate-pulse' : 'bg-[#108981]'}`}>
                {totalBadge > 9 ? '9+' : totalBadge}
              </span>
            )}
          </button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className={`relative rounded-lg ${compact ? 'h-7 w-7' : 'h-10 w-10'}`}
            data-testid="notification-center-trigger"
          >
            <Bell className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
            {totalBadge > 0 && (
              <span className={`absolute -top-0.5 -right-0.5 rounded-full flex items-center justify-center text-white font-medium ${compact ? 'min-w-[16px] h-4 px-0.5 text-[10px]' : 'w-5 h-5 text-xs'} ${hasCritical ? 'bg-red-500 animate-pulse' : 'bg-[#108981]'}`}>
                {totalBadge > 9 ? '9+' : totalBadge}
              </span>
            )}
          </Button>
        )}
      </PopoverTrigger>

      <PopoverContent
        className="w-[min(400px,calc(100vw-24px))] p-0 max-h-[85vh] flex flex-col"
        align="end"
        collisionPadding={12}
      >
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notifications
          </h3>
          <p className="text-sm text-gray-500">
            {pushUnread > 0 ? `${pushUnread} new alert${pushUnread !== 1 ? 's' : ''}` : 'No new alerts'}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* iter1111 - Bell dropdown is now Alerts + Snoozed only.
              The Tasks tab was removed because it duplicated the Task
              Bar (OpenTasksButton) with an inferior subset. Users click
              the "See open tasks" footer link to open the Task Bar
              drawer which folds every actionable reminder into one list. */}
          <TabsList className="w-full grid grid-cols-2 p-1 bg-gray-100">
            <TabsTrigger value="alerts" className="text-xs" data-testid="tab-alerts">
              Alerts {pushUnread > 0 && <Badge variant="secondary" className="ml-1 bg-[#108981]/10 text-[#108981] text-[10px] px-1 h-4">{pushUnread}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="snoozed" className="text-xs" data-testid="tab-snoozed">
              Snoozed
            </TabsTrigger>
          </TabsList>

          {/* ALERTS TAB - Push Notifications */}
          <TabsContent value="alerts" className="min-h-[200px] flex-1 overflow-y-auto p-2" data-testid="alerts-tab-content">
            {/* Enable push prompt.
                iOS 16.4+ only supports Web Push from an INSTALLED PWA
                (Add to Home Screen). If the user is on iOS Safari as a
                regular tab, show them install instructions instead of a
                permission button that will silently no-op. */}
            {pushPermission !== 'granted' && !hasNativeApp && (() => {
              const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
              const isIOS = /iPad|iPhone|iPod/.test(ua) && !/Windows/.test(ua);
              const isStandalone = typeof window !== 'undefined' && (
                window.matchMedia?.('(display-mode: standalone)').matches ||
                window.navigator?.standalone === true
              );
              const needsInstall = isIOS && !isStandalone;
              return (
                <div className="p-3 mb-2 rounded-lg border border-[#108981]/20 bg-[#108981]/5">
                  <p className="text-sm font-medium text-[#108981] mb-1">
                    {needsInstall ? 'Install AkountFly to get alerts' : 'Enable Push Notifications'}
                  </p>
                  <p className="text-xs text-gray-500 mb-2">
                    {needsInstall
                      ? 'On iPhone/iPad, tap the Share button in Safari then "Add to Home Screen". Open AkountFly from the home screen and enable alerts here (required by iOS).'
                      : 'Get real-time alerts for payments, due bills, and settlements.'}
                  </p>
                  {!needsInstall && (
                    <Button
                      size="sm"
                      className="bg-[#108981] hover:bg-[#0d7069] text-white w-full"
                      onClick={handleEnablePush}
                      data-testid="enable-push-btn"
                    >
                      <Bell className="w-3.5 h-3.5 mr-1.5" />
                      Enable Notifications
                    </Button>
                  )}
                </div>
              );
            })()}

            {pushNotifs.length > 0 ? (
              <>
                {pushUnread > 0 && (
                  <button
                    onClick={handleMarkPushRead}
                    className="text-xs text-[#108981] hover:underline mb-2 block ml-auto"
                    data-testid="mark-all-read-btn"
                  >
                    Mark all as read
                  </button>
                )}
                <div className="space-y-1.5">
                  {pushNotifs.map((notif, idx) => {
                    const config = pushTypeConfig[notif.notification_type] || pushTypeConfig.general;
                    const IconComp = config.icon;
                    return (
                      <div
                        key={notif.id || (notif.created_at + idx)}
                        className={`group relative flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                          notif.read ? 'border-gray-100 bg-white' : 'border-[#108981]/20 bg-[#108981]/5'
                        }`}
                        data-testid={`push-notif-${idx}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                          <IconComp className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => markOneRead(notif)}
                          data-testid={`push-notif-body-${idx}`}
                        >
                          <p className={`text-sm font-medium ${notif.read ? 'text-gray-600' : 'text-gray-900'}`}>
                            {notif.title}
                          </p>
                          {/* Full body always visible - no truncation - so the
                              user can actually read the notification. */}
                          <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap break-words">
                            {notif.body}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-[10px] text-gray-400">
                              {formatTimeAgo(notif.created_at)}
                            </span>
                            {notif.url && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markOneRead(notif);
                                  setOpen(false);
                                  navigate(notif.url);
                                }}
                                className="text-[11px] text-[#108981] hover:underline font-medium"
                                data-testid={`push-notif-open-${idx}`}
                              >
                                Open →
                              </button>
                            )}
                          </div>
                        </div>
                        {!notif.read && (
                          <button
                            type="button"
                            onClick={() => markOneRead(notif)}
                            title="Mark as read"
                            className="w-2 h-2 rounded-full bg-[#108981] flex-shrink-0 mt-2 hover:ring-2 hover:ring-[#108981]/30"
                            data-testid={`push-notif-mark-read-${idx}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <Bell className="w-10 h-10 mb-2" />
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs mt-1">You'll see payment, billing, and settlement alerts here</p>
              </div>
            )}
          </TabsContent>

          {/* TASKS TAB REMOVED (iter1111) - see the Task Bar (top-right pill)
              which reads /open-tasks/aggregated for the full task list. */}

          {/* SNOOZED TAB */}
          <TabsContent value="snoozed" className="min-h-[200px] flex-1 overflow-y-auto p-2">
            {data?.snoozed?.length > 0 ? (
              <div className="space-y-2">
                {data.snoozed.map((item) => (
                  <div
                    key={item.dedupe_key}
                    className="flex items-center justify-between p-3 rounded-lg border bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      <BellOff className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-600 truncate max-w-[200px]">
                          {item.dedupe_key.split(':')[0]}
                        </p>
                        <p className="text-xs text-gray-400">
                          Until {new Date(item.snoozed_until).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {item.snooze_count}x
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <BellOff className="w-10 h-10 mb-2" />
                <p className="text-sm">No snoozed items</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="p-3 border-t bg-gray-50 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 text-[#108981] hover:bg-[#108981]/10 justify-start"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
            data-testid="notifications-see-all"
          >
            <Bell className="w-4 h-4 mr-2" />
            See all
          </Button>
          {/* iter1111 - Bridge to the Task Bar. Dispatches the custom
              event `open-tasks-open` which OpenTasksButton listens for
              and opens its Sheet drawer. Badge reflects the current
              task count so users see when work is waiting there. */}
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-700 hover:bg-slate-100"
            onClick={() => {
              setOpen(false);
              try {
                window.dispatchEvent(new CustomEvent('open-tasks-open', {
                  detail: { source: 'notification-bell' }
                }));
              } catch { /* no-op */ }
            }}
            data-testid="notifications-open-tasks-link"
          >
            <ClipboardList className="w-4 h-4 mr-1.5" />
            Open tasks
            {activeCount > 0 && (
              <Badge variant="secondary" className="ml-1.5 bg-amber-100 text-amber-700 text-[10px] px-1 h-4">
                {activeCount}
              </Badge>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-500"
            onClick={() => {
              setOpen(false);
              navigate('/settings?tab=notifications');
            }}
            data-testid="notifications-preferences-link"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Helper: format time ago
function formatTimeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

/**
 * Report Warning Ribbon
 * 
 * Shows warning in reports when pending schedule items exist
 */
export const PendingScheduleWarning = ({ className = '' }) => {
  const [hasPending, setHasPending] = useState(false);
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    const checkPending = async () => {
      try {
        const response = await axios.get(`${API}/notifications/setup-tasks`);
        const tasks = response.data.tasks || [];
        const financialTasks = tasks.filter(t => 
          ['loans', 'investments', 'fixed_assets', 'prepayments'].includes(t.source_module) &&
          t.reason_codes?.some(r => r.includes('affects_pnl'))
        );
        setHasPending(financialTasks.length > 0);
        setCount(financialTasks.length);
      } catch (error) {
        console.error('Failed to check pending schedules:', error);
      }
    };
    
    checkPending();
  }, []);
  
  if (!hasPending) return null;
  
  return (
    <div 
      className={`flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm ${className}`}
      data-testid="pending-schedule-warning"
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <span>
        <strong>{count} recurring item{count > 1 ? 's have' : ' has'} not been fully configured.</strong>
        {' '}Your results may be incomplete.
      </span>
    </div>
  );
};

/**
 * User Mode Toggle (Business vs Accountant)
 */
export const UserModeToggle = ({ mode, onChange }) => {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={mode === 'business' ? 'font-medium' : 'text-gray-400'}>Business</span>
      <button
        onClick={() => onChange(mode === 'business' ? 'accountant' : 'business')}
        className={`relative w-12 h-6 rounded-full transition-colors ${mode === 'accountant' ? 'bg-[#108981]' : 'bg-gray-300'}`}
      >
        <span 
          className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${mode === 'accountant' ? 'translate-x-7' : 'translate-x-1'}`} 
        />
      </button>
      <span className={mode === 'accountant' ? 'font-medium' : 'text-gray-400'}>Accountant</span>
    </div>
  );
};

// Export all components
export default {
  SetupTasksPanel,
  MonthEndActionCenter,
  NotificationCenter,
  PendingScheduleWarning,
  UserModeToggle
};
